import { Button } from '@/chassis/ui/components/action';
import Lucide from '@/chassis/ui/components/icons/Lucide';

type DocumentActionButtonsProps = {
  url?: string;
  fileName?: string;
  onOpen?: () => void | Promise<void>;
  onDownload?: () => void | Promise<void>;
  onRemove?: () => void | Promise<void>;
  openLabel?: string;
  downloadLabel?: string;
  removeLabel?: string;
  size?: 'sm' | 'lg';
  className?: string;
};

const buildFallbackDownloadUrl = (rawUrl: string): string | null => {
  try {
    const parsed = new URL(rawUrl, window.location.origin);
    const match = parsed.pathname.match(/\/uploadeddocuments\/([^/]+)\/download$/i);
    if (!match?.[1]) {
      return null;
    }

    const documentRef = decodeURIComponent(match[1]);
    if (!documentRef.includes(':')) {
      return null;
    }

    const normalizedId = documentRef.split(':').slice(1).join(':').trim();
    if (!normalizedId) {
      return null;
    }

    parsed.pathname = parsed.pathname.replace(match[1], encodeURIComponent(normalizedId));
    return parsed.toString();
  } catch {
    return null;
  }
};

export default function DocumentActionButtons({
  url,
  fileName,
  onOpen,
  onDownload,
  onRemove,
  openLabel = 'Abrir',
  downloadLabel = 'Baixar',
  removeLabel = 'Remover',
  size = 'sm',
  className,
}: DocumentActionButtonsProps) {
  const hasActions = !!(url || onOpen || onDownload || onRemove);
  const actionsDisabled = !hasActions;

  const buildAuthHeaders = (): HeadersInit => {
    const token = localStorage.getItem('auth.token');
    const headers: Record<string, string> = {
      Accept: '*/*',
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  };

  const parseFileNameFromDisposition = (contentDisposition: string | null): string | null => {
    if (!contentDisposition) return null;
    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      try {
        return decodeURIComponent(utf8Match[1]);
      } catch {
        return utf8Match[1];
      }
    }
    const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    return asciiMatch?.[1] ?? null;
  };

  const fetchDocumentBlob = async (): Promise<{ blobUrl: string; suggestedFileName: string }> => {
    if (!url) {
      throw new Error('URL do documento não informada');
    }

    let response = await fetch(url, {
      method: 'GET',
      headers: buildAuthHeaders(),
      credentials: 'include',
    });

    // Fallback para referências no formato RESOURCE:id (ex.: DOCUMENT:uuid).
    if (!response.ok && (response.status === 400 || response.status === 404)) {
      const fallbackUrl = buildFallbackDownloadUrl(url);
      if (fallbackUrl && fallbackUrl !== url) {
        const retryResponse = await fetch(fallbackUrl, {
          method: 'GET',
          headers: buildAuthHeaders(),
          credentials: 'include',
        });
        if (retryResponse.ok) {
          response = retryResponse;
        }
      }
    }

    if (!response.ok) {
      throw new Error(`Falha ao obter documento (${response.status})`);
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const fileNameFromHeader = parseFileNameFromDisposition(response.headers.get('content-disposition'));

    return {
      blobUrl,
      suggestedFileName: fileNameFromHeader || fileName || 'documento',
    };
  };

  const handleOpen = async () => {
    try {
      if (onOpen) {
        await onOpen();
        return;
      }
      const { blobUrl } = await fetchDocumentBlob();
      window.open(blobUrl, '_blank', 'noopener,noreferrer')?.focus?.();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (error) {
      console.error('Nao foi possivel abrir o documento.', error);
    }
  };

  const handleDownload = async () => {
    try {
      if (onDownload) {
        await onDownload();
        return;
      }
      const { blobUrl, suggestedFileName } = await fetchDocumentBlob();
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = suggestedFileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5_000);
    } catch (error) {
      console.error('Nao foi possivel baixar o documento.', error);
    }
  };

  const handleRemove = async () => {
    if (!onRemove) {
      return;
    }

    try {
      await onRemove();
    } catch (error) {
      console.error('Nao foi possivel remover o documento.', error);
    }
  };

  return (
    <div className={className || 'ml-auto flex items-center gap-2'}>
      <Button
        type="button"
        variant="outline-primary"
        size={size}
        className="flex items-center gap-1 text-sm"
        disabled={actionsDisabled}
        onClick={() => {
          void handleOpen();
        }}
      >
        <Lucide icon="ExternalLink" className="h-4 w-4" />
        <span>{openLabel}</span>
      </Button>
      <Button
        type="button"
        variant="outline-secondary"
        size={size}
        className="flex items-center gap-1 text-sm"
        disabled={actionsDisabled}
        onClick={() => {
          void handleDownload();
        }}
      >
        <Lucide icon="Download" className="h-4 w-4" />
        <span>{downloadLabel}</span>
      </Button>
      {onRemove ? (
        <Button
          type="button"
          variant="outline-danger"
          size={size}
          className="flex items-center gap-1 text-sm"
          onClick={() => {
            void handleRemove();
          }}
        >
          <Lucide icon="Trash2" className="h-4 w-4" />
          <span>{removeLabel}</span>
        </Button>
      ) : null}
    </div>
  );
}
